"use client";

import { ComponentProps, FC } from "react";
import { Content } from "@builder.io/sdk-react";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";

import {
  customBuilderContextFunctions,
  CustomBuilderContextFunctionNamesType,
} from "@/libs/builder/builderApi";

type Props = ComponentProps<typeof Content>;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 60,
      gcTime: 1000 * 60 * 60,
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
});

const CACHED_FUNCTIONS = [
  "validateEmail",
  "validateUniqueEmail",
  "validateFreeEmailDomain",
  "validatePostalCode",
  "validateUniqueCompany",
  "validatePhone",
  "validateUniqueNickname",
] satisfies CustomBuilderContextFunctionNamesType[];

type CachedFunctionName = (typeof CACHED_FUNCTIONS)[number];

const INVALIDATED_CACHED_FUNCTIONS_ON_SUBMIT = [
  "validateUniqueCompany",
  "validateUniqueEmail",
  "validateUniqueNickname",
] satisfies CachedFunctionName[];

type InvalidatedCachedFunctionName =
  (typeof INVALIDATED_CACHED_FUNCTIONS_ON_SUBMIT)[number];

const SUBMIT_FUNCTIONS = [
  "submitRegistration",
  "submitQuoteRegistration",
  "submitSolarwindsCustomerProfessional",
  "submitTrustCenterRegistration",
] satisfies CustomBuilderContextFunctionNamesType[];

const INVALIDATION_FIELD_NAMES: Record<
  InvalidatedCachedFunctionName,
  string[]
> = {
  validateUniqueEmail: ["email", "Email", "WorkEmail", "PartnerEmail"],
  validateUniqueCompany: ["company", "Company"],
  validateUniqueNickname: ["nickname", "Nickname"],
};

const asRecord = (value: unknown) =>
  typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : undefined;

const getSubmittedValues = (body: unknown, fieldNames: string[]): string[] => {
  const root = asRecord(body);
  const sources = [root, asRecord(root?.submission)];

  const values = sources
    .flatMap((source) => fieldNames.map((fieldName) => source?.[fieldName]))
    .filter(
      (value): value is string => typeof value === "string" && value !== "",
    );

  return [...new Set(values)];
};

/**
 * Maps cached function names to parameter positions that should be excluded from cache keys.
 * Used to exclude requestId and correlationId parameters.
 */
const CACHE_KEY_EXCLUDED_POSITIONS: Partial<
  Record<CachedFunctionName, number[]>
> = {
  validateEmail: [1, 2],
  validatePhone: [2, 3],
  validateUniqueEmail: [2],
  validateUniqueCompany: [2],
  validateUniqueNickname: [3],
};

const getFilteredArgsForCacheKey = (
  fnName: CachedFunctionName,
  args: unknown[],
): unknown[] => {
  const excludedPositions = CACHE_KEY_EXCLUDED_POSITIONS[fnName] || [];
  return args.filter((_, index) => !excludedPositions.includes(index));
};

const BuilderContentWithCacheInner: FC<Props> = (props) => {
  const cachedContext = {
    ...props.context,
    ...customBuilderContextFunctions,
  };

  CACHED_FUNCTIONS.forEach((fnName) => {
    const originalFn = customBuilderContextFunctions[fnName];
    if (originalFn && typeof originalFn === "function") {
      cachedContext[fnName] = async (...args: unknown[]) => {
        const filteredArgs = getFilteredArgsForCacheKey(fnName, args);

        return queryClient.fetchQuery({
          queryKey: [fnName, ...filteredArgs],
          queryFn: () =>
            (originalFn as (...functionArgs: unknown[]) => unknown)(...args),
        });
      };
    }
  });

  // Invalidate unique validations on submit
  SUBMIT_FUNCTIONS.forEach((fnName) => {
    const originalFn = customBuilderContextFunctions[fnName];
    if (originalFn && typeof originalFn === "function") {
      cachedContext[fnName] = async (...args: unknown[]) => {
        const result = await (
          originalFn as (...functionArgs: unknown[]) => unknown
        )(...args);

        Object.entries(INVALIDATION_FIELD_NAMES).forEach(
          ([invalidatedFnName, fieldNames]) => {
            getSubmittedValues(args[0], fieldNames).forEach((value) => {
              // Prefix match: the validated value is always the first cache key argument.
              queryClient.invalidateQueries({
                queryKey: [invalidatedFnName, value],
              });
            });
          },
        );

        return result;
      };
    }
  });

  return <Content {...props} context={cachedContext} />;
};

export const BuilderContentWithApiCache: FC<Props> = (props) => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister }}
    >
      <BuilderContentWithCacheInner {...props} />
    </PersistQueryClientProvider>
  );
};
